import { useEffect, useState } from "react";
import api, { formatEUR, formatDateFR, currentMonth } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import AddTransactionDialog from "@/components/AddTransactionDialog";
import { toast } from "sonner";

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterCat, setFilterCat] = useState("all");
  const [month, setMonth] = useState(currentMonth());

  const load = async () => {
    const params = new URLSearchParams({ month });
    if (filterType !== "all") params.set("type", filterType);
    if (filterCat !== "all") params.set("category_id", filterCat);
    const [t, a, c] = await Promise.all([
      api.get(`/transactions?${params}`),
      api.get("/accounts"),
      api.get("/categories"),
    ]);
    setTxs(t.data);
    setAccounts(a.data);
    setCategories(c.data);
  };

  useEffect(() => { load(); }, [filterType, filterCat, month]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette transaction ?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Supprimée");
    load();
  };

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
  const accMap = Object.fromEntries(accounts.map((a) => [a.id, a]));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Transactions</h1>
          <p className="text-stone-500 mt-1">Toutes vos entrées et sorties</p>
        </div>
        <AddTransactionDialog accounts={accounts} categories={categories} onCreated={load} />
      </div>

      <Card className="p-4 rounded-2xl border-stone-200">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-stone-500 font-medium">Mois</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                   className="mt-1 w-full rounded-xl border border-stone-200 h-10 px-3 text-sm bg-white" data-testid="filter-month" />
          </div>
          <div>
            <label className="text-xs text-stone-500 font-medium">Type</label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="mt-1 rounded-xl h-10" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="income">Revenus</SelectItem>
                <SelectItem value="expense">Dépenses</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-stone-500 font-medium">Catégorie</label>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="mt-1 rounded-xl h-10" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        {txs.length === 0 ? (
          <div className="py-16 text-center text-stone-400 text-sm">Aucune transaction pour ces filtres</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {txs.map((t) => {
              const cat = catMap[t.category_id];
              const acc = accMap[t.account_id];
              return (
                <div key={t.id} className="flex items-center gap-4 px-6 py-4 hover:bg-stone-50 transition-colors group" data-testid={`tx-row-${t.id}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: (cat?.color || "#78716C") + "20", color: cat?.color || "#78716C" }}>
                    <span className="text-xs font-bold">{cat?.name?.slice(0, 2).toUpperCase() || "??"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{t.description || cat?.name || "Transaction"}</div>
                    <div className="text-xs text-stone-500">{formatDateFR(t.date)} · {cat?.name} · {acc?.name}</div>
                  </div>
                  <div className={`font-semibold font-mono-num text-sm ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.type === "income" ? "+" : "−"}{formatEUR(t.amount)}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(t.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`tx-delete-${t.id}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
